import { useState, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";

interface TooltipProps {
  label: string;
  children: ReactNode;
  enabled?: boolean;
}

export function SidebarTooltip({
  label,
  children,
  enabled = true,
}: TooltipProps) {
  const [rect, setRect] = useState<DOMRect | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  if (!enabled) {
    return <>{children}</>;
  }

  function handleMouseEnter() {
    if (triggerRef.current) {
      setRect(triggerRef.current.getBoundingClientRect());
    }
  }

  function handleMouseLeave() {
    setRect(null);
  }

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: tooltip trigger wrapper needs mouse events
    <div
      ref={triggerRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      <AnimatePresence>
        {rect &&
          createPortal(
            <motion.div
              initial={{ opacity: 0, x: -4 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -4 }}
              transition={{ duration: 0.15, ease: "easeOut" }}
              className="fixed z-50 rounded-md bg-gray-900 px-2.5 py-1 text-[11px] font-medium tracking-[-0.01em] text-white shadow-lg shadow-gray-900/10 pointer-events-none"
              style={{
                left: rect.right + 10,
                top: rect.top + rect.height / 2,
                transform: "translateY(-50%)",
              }}
            >
              {label}
            </motion.div>,
            document.body,
          )}
      </AnimatePresence>
    </div>
  );
}
